import axios from "axios";
import { ActionType } from "../actions";
import PluginService from "../../services/pluginService";
import { IASOResult } from "../../models/configuration/plugin/IConfiguration";
import { setConfigIds } from "../appSlice";
import ElliHost from "../../services/elliHost";
import { AppDispatch } from "../store";

export const ASO_NOT_CONFIGURED_MESSAGE = "Your account is not configured to utilize Automated Service Ordering (ASO) with Docutech. Please reach out to your Docutech account manager if you wish to utilize this feature.";

// Requests for different configuration Ids can overlap (initial load, template config, header switch);
// only the most recent request may update state, otherwise a slower older response overwrites it.
let latestRequestId = 0;

const getASOConfigPlugInData = (configId?: string): (dispatch: AppDispatch) => Promise<void> => {
    return async (dispatch: AppDispatch) => {
        const requestId = ++latestRequestId;
        const isStale = () => requestId !== latestRequestId;
        dispatch({ type: ActionType.GET_ASO_CONFIG_PLUGIN_DATA });
        const elliHost = ElliHost.getInstance();
        try {
            const instance = await elliHost.getInstanceId();
            if (isStale()) return;
            if (!instance || !instance.instanceId) {
                const payloadErrorMessage = "Failed to fetch Instance ID for ASO Config Plugin Data.";
                console.error(payloadErrorMessage);
                dispatch({ type: ActionType.GET_ASO_CONFIG_PLUGIN_DATA_FAILURE, payload: payloadErrorMessage });
                return;
            }
            const payload = await new PluginService().getEpcASOConfig<IASOResult>("aso", instance.instanceId, configId);
            if (isStale()) return;

            // Manage local cache of configurationIds - write if absent, else compare and update if different
            const fetchedIds: string[] = payload?.Result?.ConfigurationIds ?? [];
            try {
                if (fetchedIds && fetchedIds.length > 0) {
                    dispatch(setConfigIds(fetchedIds));
                }
            } catch (e) {
                console.warn("Failed to read/update configurationIds in localStorage:", e);
            }

            // Store ASO plugin config in its own slice
            dispatch({
                type: ActionType.GET_ASO_CONFIG_PLUGIN_DATA_SUCCESS,
                payload: payload.Result
            });
        }
        catch (error: unknown) {
            if (isStale()) return;
            console.error("Get ASO Config Plugin Data");
            let payloadErrorMessage = "Failed to fetch ASO Config Plugin Data.";
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 404) {
                    payloadErrorMessage = ASO_NOT_CONFIGURED_MESSAGE;
                } else {
                    payloadErrorMessage = `Failed to fetch ASO Config Plugin Data: (${error.message})`;
                }
            } else if (error instanceof Error) {
                payloadErrorMessage = `Failed to fetch ASO Config Plugin Data: (${error.message})`;
            }
            dispatch({ type: ActionType.GET_ASO_CONFIG_PLUGIN_DATA_FAILURE, payload: payloadErrorMessage });
        }
    };
};

export default getASOConfigPlugInData;